import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Suspense, lazy, useEffect } from 'react'
import { ThemeProvider } from './contexts/ThemeContext'
import { SiteSettingsProvider } from './contexts/SiteSettingsContext'
import { CookieConsentProvider } from './contexts/CookieConsentContext'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import CookieConsent from './components/CookieConsent'
import MatomoAnalytics from './components/MatomoAnalytics'
import BackToTop from './components/BackToTop'
import ScrollToTop from './components/ScrollToTop'
import CanvasTone from './components/CanvasTone'
import LoadingSpinner from './components/LoadingSpinner'
import { preloadCriticalRoutes } from './utils/preloadRoutes'

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home'))
const Services = lazy(() => import('./pages/Services'))
const About = lazy(() => import('./pages/About'))
const Contact = lazy(() => import('./pages/Contact'))
const Press = lazy(() => import('./pages/Press'))
// const News = lazy(() => import('./pages/News')) // Hidden for now
// const NewsDetail = lazy(() => import('./pages/NewsDetail')) // Hidden for now
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const Careers = lazy(() => import('./pages/Careers'))
const Apply = lazy(() => import('./pages/Apply'))
const DubaiServices = lazy(() => import('./pages/DubaiServices'))
const AbuDhabiServices = lazy(() => import('./pages/AbuDhabiServices'))
const DevOpsServicesDubai = lazy(() => import('./pages/DevOpsServicesDubai'))
const KubernetesConsultingDubai = lazy(() => import('./pages/KubernetesConsultingDubai'))
const DevSecOpsDubai = lazy(() => import('./pages/DevSecOpsDubai'))
const StartupTechConsultingDubai = lazy(() => import('./pages/StartupTechConsultingDubai'))
const AWSCloudConsultingDubai = lazy(() => import('./pages/AWSCloudConsultingDubai'))
const SoftwareDevelopmentDubai = lazy(() => import('./pages/SoftwareDevelopmentDubai'))
const ITCompaniesDubai = lazy(() => import('./pages/ITCompaniesDubai'))
const ITConsultingCompaniesDubai = lazy(() => import('./pages/ITConsultingCompaniesDubai'))
const EcommerceDevelopmentDubai = lazy(() => import('./pages/EcommerceDevelopmentDubai'))
const ManagedITServicesDubai = lazy(() => import('./pages/ManagedITServicesDubai'))
const Blog = lazy(() => import('./pages/Blog'))
const DubaiSoftwareTrends2026 = lazy(() => import('./pages/blog/DubaiSoftwareTrends2026'))
const WebDevelopmentAbuDhabiGuide = lazy(() => import('./pages/blog/WebDevelopmentAbuDhabiGuide'))
const WebDevelopmentAgencyDubai = lazy(() => import('./pages/blog/WebDevelopmentAgencyDubai'))
const EnterpriseSoftwareDubai = lazy(() => import('./pages/blog/EnterpriseSoftwareDubai'))
const DigitalTransformationUAE = lazy(() => import('./pages/blog/DigitalTransformationUAE'))
const MobileAppCostDubai = lazy(() => import('./pages/blog/MobileAppCostDubai'))
const HowToChooseDevOpsConsultantDubai = lazy(() => import('./pages/blog/HowToChooseDevOpsConsultantDubai'))
const CloudMigrationCostUAE = lazy(() => import('./pages/blog/CloudMigrationCostUAE'))
const NotFound = lazy(() => import('./pages/NotFound'))

function App() {
  useEffect(() => {
    preloadCriticalRoutes()
  }, [])

  return (
    <ThemeProvider>
      <SiteSettingsProvider>
        <CookieConsentProvider>
          <Router>
            <ScrollToTop />
            <CanvasTone />
            <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200">
              <MatomoAnalytics />
              <Navbar />
              <main>
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/dubai-services" element={<DubaiServices />} />
                    <Route path="/abu-dhabi-services" element={<AbuDhabiServices />} />
                    <Route path="/devops-services-dubai" element={<DevOpsServicesDubai />} />
                    <Route path="/kubernetes-consulting-dubai" element={<KubernetesConsultingDubai />} />
                    <Route path="/devsecops-dubai" element={<DevSecOpsDubai />} />
                    <Route path="/startup-tech-consulting-dubai" element={<StartupTechConsultingDubai />} />
                    <Route path="/aws-cloud-consulting-dubai" element={<AWSCloudConsultingDubai />} />
                    <Route path="/software-development-dubai" element={<SoftwareDevelopmentDubai />} />
                    <Route path="/it-companies-dubai" element={<ITCompaniesDubai />} />
                    <Route path="/it-consulting-companies-dubai" element={<ITConsultingCompaniesDubai />} />
                    <Route path="/ecommerce-development-dubai" element={<EcommerceDevelopmentDubai />} />
                    <Route path="/managed-it-services-dubai" element={<ManagedITServicesDubai />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/dubai-software-trends-2026" element={<DubaiSoftwareTrends2026 />} />
                    <Route path="/blog/web-development-abu-dhabi-guide" element={<WebDevelopmentAbuDhabiGuide />} />
                    <Route path="/blog/web-development-agency-dubai" element={<WebDevelopmentAgencyDubai />} />
                    <Route path="/blog/enterprise-software-dubai" element={<EnterpriseSoftwareDubai />} />
                    <Route path="/blog/digital-transformation-uae" element={<DigitalTransformationUAE />} />
                    <Route path="/blog/mobile-app-development-cost-dubai" element={<MobileAppCostDubai />} />
                    <Route path="/blog/how-to-choose-devops-consultant-dubai" element={<HowToChooseDevOpsConsultantDubai />} />
                    <Route path="/blog/cloud-migration-cost-uae" element={<CloudMigrationCostUAE />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/press" element={<Press />} />
                    {/* <Route path="/news" element={<News />} /> */}
                    {/* <Route path="/news/:slug" element={<NewsDetail />} /> */}
                    <Route path="/careers" element={<Careers />} />
                    <Route path="/apply" element={<Apply />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </main>
              <Footer />
              <CookieConsent />
              <BackToTop />
            </div>
          </Router>
        </CookieConsentProvider>
      </SiteSettingsProvider>
    </ThemeProvider>
  )
}

export default App